import { Component, Input, OnInit } from '@angular/core';
import { ControlsOf, FormGroup } from '@ngneat/reactive-forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  take,
} from 'rxjs';

import { Auth0UserMetadata, UserInvite } from '@digital-platform/users/domain';
import { UserRolesInputField } from '@digital-platform/users/domain';
import {
  CompanyNameService,
  LoginSessionFacadeService,
  UserInviteService,
} from '@digital-platform/users/services';

type UserInviteForm = Omit<UserInvite, 'email'>;
type InvitationForm = ControlsOf<UserInviteForm>;

@Component({
  selector: 'digital-platform-invite-user-form',
  templateUrl: './invite-user-form.component.html',
  styleUrls: ['./invite-user-form.component.scss'],
})
@UntilDestroy()
export class InviteUserFormComponent implements OnInit {
  @Input() form: FormGroup<InvitationForm>;
  @Input() user: Auth0UserMetadata;
  showCompanyDropdown = false;
  loading = false;

  companyNames: string[] = [];
  customerOptionDisabled = false;
  customerManifestOptionDisabled = false;
  samsaraOptionDisabled = false;
  salesOptionDisabled = false;
  coordinatorOptionDisabled = false;
  analystOptionDisabled = false;
  scaleAttendantOptionDisabled = false;
  showRolesSelection = false;
  isCompanyDisabled = true;
  filteredCompany;

  constructor(
    private inviteService: UserInviteService,
    private companyNameService: CompanyNameService,
    private facade: LoginSessionFacadeService
  ) {
    this.inviteService.loading$.subscribe(
      (loading) => (this.loading = loading)
    );
    this.companyNameService.getCompanyNameList();
    this.facade.loginSession$
      .pipe(
        filter((session) => !!session),
        take(1)
      )
      .subscribe((session) => {
        this.showCompanyDropdown = true;
        if (this.hasInvitePermissions(session)) {
          this.handleCompanyNames(session);
        }
      });
  }

  private hasInvitePermissions(session: any): boolean {
    return (
      session.user.permissions.indexOf('invite:other') > -1 ||
      session.user.permissions.indexOf('invite:all') > -1
    );
  }

  private handleCompanyNames(session: any): void {
    this.companyNameService.companyNameList$
      .pipe(untilDestroyed(this))
      .subscribe((response) => {
        this.companyNames = response;
        if (this.companyNames?.length > 0) {
          this.isCompanyDisabled = false;
        }
        if (session.user.permissions.indexOf('invite:all') > -1) {
          this.roleSelectionAllowed();
        } else {
          this.roleSelectionDisallowed();
        }
      });
  }

  private roleSelectionAllowed(): void {
    this.showRolesSelection = true;
    if (!this.hasAllCustomers(this.companyNames)) {
      this.companyNames?.unshift('*');
    } else {
      this.updateCompanyNames();
    }
  }

  private roleSelectionDisallowed(): void {
    this.showRolesSelection = false;
    this.form?.controls?.roles.setValue(['Customer']);
    if (this.hasAllCustomers(this.companyNames)) {
      const index = this.companyNames?.indexOf('*');
      if (index > -1) {
        this.companyNames.splice(index, 1);
      }
    }
  }

  private updateCompanyNames(): void {
    const index = this.companyNames?.indexOf('*');
    if (index > -1) {
      this.companyNames?.splice(index, 1);
    }
    this.companyNames?.unshift('*');
  }

  ngOnInit(): void {
    const { first_name, last_name } = this.user ?? {};

    if (first_name || last_name) {
      if (first_name) {
        this.form?.controls?.first_name.setValue(first_name);
      }
      if (last_name) {
        this.form?.controls?.last_name.setValue(last_name);
      }

      this.form?.controls?.first_name.clearValidators();
      this.form?.controls?.last_name.clearValidators();
    }

    this.filteredCompany = this.form?.controls?.companyName.valueChanges.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      startWith(''),
      map((company) => this._filter(company || ''))
    );
  }

  private _filter(value: string): string[] {
    if (value.length >= 2) {
      const filterValue = this._normalizeValue(value);

      return this.companyNames?.filter((company) =>
        this._normalizeValue(company).includes(filterValue)
      );
    }
    return [];
  }

  private _normalizeValue(value: string): string {
    return value.toLowerCase().replace(/\s/g, '');
  }

  private hasAllCustomers(customers: string[]): boolean {
    if (customers?.length > 1 && customers.includes('*')) {
      return true;
    } else {
      return false;
    }
  }

  public disableOptions(value: string[]) {
    if (value?.length >= 1) {
      switch (value[0]) {
        case UserRolesInputField.Reset: {
          this.customerOptionDisabled = false;
          this.customerManifestOptionDisabled = false;
          this.samsaraOptionDisabled = false;
          this.salesOptionDisabled = false;
          this.coordinatorOptionDisabled = false;
          this.analystOptionDisabled = false;
          this.scaleAttendantOptionDisabled = false;
          this.form.controls.roles.reset();
          this.form.controls['companyName'].reset();
          this.form.controls['companyName'].enable();
          break;
        }
        case UserRolesInputField['Special Waste Customer']:
        case UserRolesInputField[
          'Special Waste Customer - Manifest Visibility'
        ]:
        case UserRolesInputField['Special Waste Samsara']: {
          this.salesOptionDisabled = true;
          this.coordinatorOptionDisabled = true;
          this.scaleAttendantOptionDisabled = true;
          this.analystOptionDisabled = true;
          break;
        }
        case UserRolesInputField['Special Waste Sales']: {
          this.customerOptionDisabled = true;
          this.customerManifestOptionDisabled = true;
          this.samsaraOptionDisabled = true;
          this.coordinatorOptionDisabled = true;
          this.analystOptionDisabled = true;
          this.scaleAttendantOptionDisabled = true;
          this.form.controls['companyName'].setValue('*');
          this.form.controls['companyName'].disable();
          break;
        }
        case UserRolesInputField['Special Waste Coordinator']: {
          this.customerOptionDisabled = true;
          this.customerManifestOptionDisabled = true;
          this.samsaraOptionDisabled = true;
          this.salesOptionDisabled = true;
          this.analystOptionDisabled = true;
          this.scaleAttendantOptionDisabled = true;
          this.form.controls['companyName'].setValue('*');
          this.form.controls['companyName'].disable();
          break;
        }
        case UserRolesInputField['Special Waste Analyst']: {
          this.customerOptionDisabled = true;
          this.customerManifestOptionDisabled = true;
          this.samsaraOptionDisabled = true;
          this.salesOptionDisabled = true;
          this.coordinatorOptionDisabled = true;
          this.scaleAttendantOptionDisabled = true;
          this.form.controls['companyName'].setValue('*');
          this.form.controls['companyName'].disable();
          break;
        }
        case UserRolesInputField['Special Waste Scale Attendant']: {
          this.customerOptionDisabled = true;
          this.customerManifestOptionDisabled = true;
          this.samsaraOptionDisabled = true;
          this.salesOptionDisabled = true;
          this.coordinatorOptionDisabled = true;
          this.analystOptionDisabled = true;
          break;
        }
      }
    }
  }

  errorHandling = (controlName: keyof InvitationForm, error: string) => {
    if (!this.form) {
      return false;
    }
    const control = this.form.get(controlName);
    return control?.touched && control.hasError(error);
  };
  // check for users first name and last name are defined
  isUserDefined(): boolean {
    return (
      (this.user && !this.user?.['first_name'] && !this.user?.['last_name']) ||
      !this.user
    );
  }
}
